import type { HTMLAttributes, ReactNode, Ref } from 'react';
import { createContext } from 'react';
import type { ContextValue } from 'react-aria-components/slots';
import { cva } from 'class-variance-authority';

import { useLPContextProps } from './utils';

import styles from './styles/AvatarGroup.module.css';

const avatarGroupStyles = cva(styles.base);

interface AvatarGroupProps extends HTMLAttributes<HTMLSpanElement> {
	/**
	 * The avatars in the group. The first is the primary avatar; each one after it is badged onto
	 * the primary's corner, so pass exactly two for the badged pair this renders today.
	 */
	children: ReactNode;
	ref?: Ref<HTMLSpanElement>;
}

const AvatarGroupContext = createContext<ContextValue<AvatarGroupProps, HTMLSpanElement>>(null);

/**
 * An avatar group shows avatars that are related to each other. Today it renders one layout: a
 * primary avatar with a second, smaller one badged onto its corner, for when two parties share
 * responsibility for one thing.
 *
 * ```tsx
 * <AvatarGroup aria-label="Ada Lovelace, with Vega">
 *   <Avatar aria-label="Ada Lovelace">AL</Avatar>
 *   <Avatar size="small" src={vegaIconUrl} alt="Vega" />
 * </AvatarGroup>
 * ```
 *
 * Children set their own `size` — the two avatars are deliberately different sizes, so the group
 * does not impose one. The badge is absolutely positioned, so the group takes up exactly the
 * primary avatar's space no matter what is badged onto it. That keeps rows aligned in a vertical
 * list, which is the reason to reach for this over a side-by-side stack.
 *
 * The badge can be any node, not just an `Avatar` — a logo mark works too.
 *
 * Renders a `span` rather than a `div`, so it stays valid inside a heading or any other
 * phrasing-content-only parent. It is `display: inline-flex`, so it lays out the same either way.
 *
 * Set `--lp-avatar-group-ring-color` when the group sits on something other than the primary UI
 * background, so the ring separating the two avatars still matches behind it.
 *
 * Accessibility: each avatar keeps its own label, and the group is a `role="group"` that should be
 * named with `aria-label`. If nearby text already names both parties, mark the whole group
 * `aria-hidden` instead of making a screen reader announce them twice.
 *
 * https://react-spectrum.adobe.com/AvatarGroup
 */
const AvatarGroup = ({ ref, ...props }: AvatarGroupProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, AvatarGroupContext);
	const { className, children, ...rest } = mergedProps;

	return (
		<span role="group" {...rest} ref={mergedRef} className={avatarGroupStyles({ className })}>
			{children}
		</span>
	);
};

export { AvatarGroup, AvatarGroupContext, avatarGroupStyles };
export type { AvatarGroupProps };
